import { useEffect } from 'react';
import { useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BookOpen, GraduationCap } from 'lucide-react';
import {
  FormDateField,
  FormDialog,
  FormSection,
  FormStatusField,
  FormTextField,
} from '@/components/common/FormDialog';
import { useSaveQuestionClass, useSaveQuestionSubject } from '../hooks/useQuestionMutations';
import {
  questionClassFormSchema,
  questionSubjectFormSchema,
  type QuestionClass,
  type QuestionClassFormValues,
  type QuestionSubject,
  type QuestionSubjectFormValues,
} from '../types/question.types';

const CLASS_DEFAULTS: QuestionClassFormValues = {
  name: '',
  code: '',
  nameLocal: '',
  description: '',
  slNum: '',
  isActive: true,
  activeFrom: '',
  activeTo: '',
};

const SUBJECT_DEFAULTS: QuestionSubjectFormValues = {
  name: '',
  code: '',
  nameLocal: '',
  description: '',
  slNum: '',
  isActive: true,
  activeFrom: '',
  activeTo: '',
};

const OPTIONAL_PLACEHOLDER = 'Optional';

const toDateInput = (value: string | null): string | undefined => {
  if (value === null) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? undefined
    : `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(
        parsed.getDate(),
      ).padStart(2, '0')}`;
};

const numberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

interface QuestionCatalogueFieldsProps {
  control: Control<QuestionClassFormValues>;
  namePlaceholder: string;
  codePlaceholder: string;
  isCodeNumeric: boolean;
}

function QuestionCatalogueFields({
  control,
  namePlaceholder,
  codePlaceholder,
  isCodeNumeric,
}: QuestionCatalogueFieldsProps) {
  return (
    <>
      <FormSection>
        <FormTextField
          control={control}
          name="name"
          label="Name"
          placeholder={namePlaceholder}
          isRequired
        />
        <FormTextField
          control={control}
          name="code"
          label="Code"
          placeholder={codePlaceholder}
          inputMode={isCodeNumeric ? 'numeric' : undefined}
          hasTabularNumbers={isCodeNumeric}
        />
        <FormTextField
          control={control}
          name="nameLocal"
          label="Local Name"
          placeholder={OPTIONAL_PLACEHOLDER}
        />
        <FormTextField
          control={control}
          name="slNum"
          label="Sl Num"
          placeholder={OPTIONAL_PLACEHOLDER}
          inputMode="numeric"
          hasTabularNumbers
        />
        <FormTextField
          control={control}
          name="description"
          label="Description"
          placeholder={OPTIONAL_PLACEHOLDER}
          isFullRow
        />
        <FormDateField control={control} name="activeFrom" label="Active From" />
        <FormDateField control={control} name="activeTo" label="Active To" />
      </FormSection>
      <FormStatusField control={control} name="isActive" />
    </>
  );
}

interface ClassModalProps {
  questionClass: QuestionClass | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuestionClassFormModal({ questionClass, open, onOpenChange }: ClassModalProps) {
  const form = useForm<QuestionClassFormValues>({
    resolver: zodResolver(questionClassFormSchema),
    defaultValues: CLASS_DEFAULTS,
  });

  useEffect(() => {
    form.reset(
      questionClass === null
        ? CLASS_DEFAULTS
        : {
            name: questionClass.name,
            code: questionClass.code ?? '',
            nameLocal: questionClass.nameLocal ?? '',
            description: questionClass.description ?? '',
            slNum: questionClass.slNum === null ? '' : String(questionClass.slNum),
            activeFrom: toDateInput(questionClass.activeFrom) ?? '',
            activeTo: toDateInput(questionClass.activeTo) ?? '',
            isActive: questionClass.isActive,
          },
    );
  }, [questionClass, open, form]);

  const save = useSaveQuestionClass(() => onOpenChange(false));

  function submit(values: QuestionClassFormValues) {
    const code = numberOrUndefined(values.code);
    const slNum = numberOrUndefined(values.slNum);
    save.mutate({
      id: questionClass?.id ?? null,
      payload: {
        name: values.name,
        isActive: values.isActive,
        ...(code === undefined ? {} : { code }),
        ...(slNum === undefined ? {} : { slNum }),
        ...(values.nameLocal.trim() === '' ? {} : { nameLocal: values.nameLocal.trim() }),
        ...(values.description.trim() === '' ? {} : { description: values.description.trim() }),
        ...(values.activeFrom === '' ? {} : { activeFrom: values.activeFrom }),
        ...(values.activeTo === '' ? {} : { activeTo: values.activeTo }),
      },
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={GraduationCap}
      title={questionClass === null ? 'Add Class' : 'Edit Class'}
      description="The classes question projects are declared against."
      form={form}
      isPending={save.isPending}
      isEditMode={questionClass !== null}
      onSubmit={submit}
      width="wide"
    >
      <QuestionCatalogueFields
        control={form.control}
        namePlaceholder="e.g. Ten"
        codePlaceholder="e.g. 10"
        isCodeNumeric
      />
    </FormDialog>
  );
}

interface SubjectModalProps {
  subject: QuestionSubject | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuestionSubjectFormModal({ subject, open, onOpenChange }: SubjectModalProps) {
  const form = useForm<QuestionSubjectFormValues>({
    resolver: zodResolver(questionSubjectFormSchema),
    defaultValues: SUBJECT_DEFAULTS,
  });

  useEffect(() => {
    form.reset(
      subject === null
        ? SUBJECT_DEFAULTS
        : {
            name: subject.name,
            code: subject.code ?? '',
            nameLocal: subject.nameLocal ?? '',
            description: subject.description ?? '',
            slNum: subject.slNum === null ? '' : String(subject.slNum),
            isActive: subject.isActive,
            activeFrom: toDateInput(subject.activeFrom) ?? '',
            activeTo: toDateInput(subject.activeTo) ?? '',
          },
    );
  }, [subject, open, form]);

  const save = useSaveQuestionSubject(() => onOpenChange(false));

  function submit(values: QuestionSubjectFormValues) {
    const slNum = numberOrUndefined(values.slNum);
    save.mutate({
      id: subject?.id ?? null,
      payload: {
        name: values.name,
        isActive: values.isActive,
        ...(values.code.trim() === '' ? {} : { code: values.code.trim() }),
        ...(slNum === undefined ? {} : { slNum }),
        ...(values.nameLocal.trim() === '' ? {} : { nameLocal: values.nameLocal.trim() }),
        ...(values.description.trim() === '' ? {} : { description: values.description.trim() }),
        ...(values.activeFrom === '' ? {} : { activeFrom: values.activeFrom }),
        ...(values.activeTo === '' ? {} : { activeTo: values.activeTo }),
      },
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={BookOpen}
      title={subject === null ? 'Add Subject' : 'Edit Subject'}
      description="The subjects a class carries, and the papers come in against."
      form={form}
      isPending={save.isPending}
      isEditMode={subject !== null}
      onSubmit={submit}
      width="wide"
    >
      <QuestionCatalogueFields
        control={form.control}
        namePlaceholder="e.g. Physics 1st Paper"
        codePlaceholder="e.g. PHY1"
        isCodeNumeric={false}
      />
    </FormDialog>
  );
}
